import { useCallback, useRef, useState } from 'react';
import useToast from 'hooks/useToast';
import { getPostMediaDeleteInfo } from 'utils/services/fileCabinetService';
import { PostMediaDeleteMode } from 'utils/types/postMediaDelete';
import { DeletePostMediaConfirmModal } from 'components/delete-post-media-confirm-modal/delete-post-media-confirm-modal';

type DeletePostFn = (postId: string, mediaDeleteMode: PostMediaDeleteMode) => Promise<void>;

interface UseDeletePostWithMediaOptions {
  onDeleted?: (postId: string) => void;
}

export function useDeletePostWithMedia(
  deletePost: DeletePostFn,
  projectId: string | number | undefined,
  options?: UseDeletePostWithMediaOptions,
) {
  const showToast = useToast();
  const onDeletedRef = useRef(options?.onDeleted);
  onDeletedRef.current = options?.onDeleted;

  const [pendingPostId, setPendingPostId] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const runDelete = useCallback(
    async (postId: string, mediaDeleteMode: PostMediaDeleteMode) => {
      setIsDeleting(true);
      try {
        await deletePost(postId, mediaDeleteMode);
        onDeletedRef.current?.(postId);
        setPendingPostId(null);
      } catch (error) {
        showToast({
          type: 'error',
          message: `Error deleting post: ${error instanceof Error ? error.message : String(error)}`,
        });
      } finally {
        setIsDeleting(false);
      }
    },
    [deletePost, showToast],
  );

  const handleDeletePost = useCallback(
    async (postId: string) => {
      if (!projectId) return;

      setIsChecking(true);
      try {
        const info = await getPostMediaDeleteInfo(projectId, postId);

        if (!info.hasMedia || !info.requiresConfirmation) {
          await runDelete(postId, 'unlink');
          return;
        }

        setPendingPostId(postId);
      } catch (error) {
        showToast({
          type: 'error',
          message: `Error checking post media: ${error instanceof Error ? error.message : String(error)}`,
        });
      } finally {
        setIsChecking(false);
      }
    },
    [projectId, runDelete, showToast],
  );

  const handleCloseModal = useCallback(() => {
    if (!isDeleting) {
      setPendingPostId(null);
    }
  }, [isDeleting]);

  const deletePostMediaConfirmModal = (
    <DeletePostMediaConfirmModal
      open={pendingPostId !== null}
      isLoading={isDeleting}
      onClose={handleCloseModal}
      onDeletePostOnly={() => pendingPostId && runDelete(pendingPostId, 'unlink')}
      onDeleteAll={() => pendingPostId && runDelete(pendingPostId, 'delete')}
    />
  );

  return {
    handleDeletePost,
    deletePostMediaConfirmModal,
    isDeletePostLoading: isChecking || isDeleting,
  };
}
